// @mention text rules, mirroring the backend parser
// (backend/blorp/utils/mentions/parsing.py):
//
//   (?<![\w@./:+-])@([\w.+-]+)
//
// A token starts at an "@" not preceded by a word character or @ . / : + -
// (so e-mail addresses and URLs aren't mentions) and runs over word
// characters and . + -. Its candidates are the token, then the token with a
// trailing ".", "+" or "-" removed, one at a time; the longest candidate the
// server resolved wins.
//
// The server decides who is mentioned. Clients only link the tokens it
// returned in `mentions: [{ user_id, username, token }]` (token: the name as
// written, lowercased, no "@"); an "@name" it didn't return stays plain text.
//
// Python's \w (Unicode letters, digits, underscore) is \p{L}\p{N}_ here. No
// lookbehind: older Safari versions can't parse it.

const WORD = "\\p{L}\\p{N}_";
const TOKEN_RE = new RegExp(`@([${WORD}.+\\-]+)`, "gu");
const TOKEN_CHAR_RE = new RegExp(`^[${WORD}.+\\-]$`, "u");
const NOT_BEFORE_RE = new RegExp(`^[${WORD}@./:+\\-]$`, "u");
const TRAILING_PUNCTUATION = ".+-";

export const MAX_USERNAME_LENGTH = 150;

// The code point ending just before `index` (a surrogate pair is one).
function charBefore(text, index) {
  if (index <= 0) return "";
  const code = text.charCodeAt(index - 1);
  if (index >= 2 && code >= 0xdc00 && code <= 0xdfff) {
    const high = text.charCodeAt(index - 2);
    if (high >= 0xd800 && high <= 0xdbff) return text.slice(index - 2, index);
  }
  return text[index - 1];
}

function charAt(text, index) {
  const point = text.codePointAt(index);
  return point == null ? "" : String.fromCodePoint(point);
}

const isTokenChar = (char) => TOKEN_CHAR_RE.test(char);

function canStartMention(text, atIndex) {
  const before = charBefore(text, atIndex);
  return !before || !NOT_BEFORE_RE.test(before);
}

// Every mention token in `text`: { start (the "@"), end, raw (without "@") }.
export function mentionTokens(text) {
  const tokens = [];
  if (!text) return tokens;
  TOKEN_RE.lastIndex = 0;
  let match;
  while ((match = TOKEN_RE.exec(text)) !== null) {
    if (canStartMention(text, match.index)) {
      tokens.push({ start: match.index, end: match.index + match[0].length, raw: match[1] });
    } else {
      // A token never contains "@", so the next one can't start inside this one.
      TOKEN_RE.lastIndex = match.index + 1;
    }
  }
  return tokens;
}

// A token's candidate usernames as written, longest first.
export function candidates(raw) {
  const found = [];
  let token = raw;
  while (token) {
    if (token.length <= MAX_USERNAME_LENGTH) found.push(token);
    if (!TRAILING_PUNCTUATION.includes(token[token.length - 1])) break;
    token = token.slice(0, -1);
  }
  return found;
}

// `text` split into [{ type: "text", text }] and [{ type: "mention", text,
// mention }] segments, where a mention segment is "@" plus the candidate the
// server resolved (its trailing punctuation stays text).
export function mentionSegments(text, mentions) {
  if (!text) return [];
  const byToken = new Map((mentions || []).map((m) => [m.token, m]));
  if (byToken.size === 0) return [{ type: "text", text }];

  const segments = [];
  let cursor = 0;
  const pushText = (end) => {
    if (end > cursor) segments.push({ type: "text", text: text.slice(cursor, end) });
  };

  for (const token of mentionTokens(text)) {
    for (const candidate of candidates(token.raw)) {
      const mention = byToken.get(candidate.toLowerCase());
      if (mention) {
        pushText(token.start);
        const end = token.start + 1 + candidate.length;
        segments.push({ type: "mention", text: text.slice(token.start, end), mention });
        cursor = end;
        break;
      }
    }
  }
  pushText(text.length);
  return segments;
}

// The first `limit` UTF-16 units of `segments` (as String#slice counted
// them before). A mention that doesn't fit whole is dropped rather than cut,
// and a cut never splits a surrogate pair. → { segments, truncated }
export function truncateSegments(segments, limit) {
  const kept = [];
  let used = 0;
  for (const segment of segments) {
    const room = limit - used;
    if (segment.text.length <= room) {
      kept.push(segment);
      used += segment.text.length;
      continue;
    }
    if (segment.type === "text" && room > 0) {
      let cut = room;
      const code = segment.text.charCodeAt(cut - 1);
      if (code >= 0xd800 && code <= 0xdbff) cut -= 1;
      if (cut > 0) kept.push({ type: "text", text: segment.text.slice(0, cut) });
    }
    return { segments: kept, truncated: true };
  }
  return { segments: kept, truncated: false };
}

// The mention being typed at the caret, for autocomplete: { start (the "@"),
// end (the end of the token, which may run past the caret), query (from
// after "@" to the caret) }, or null. The query needs at least one character.
export function activeMention(text, caret) {
  if (!text || caret == null || caret < 1 || caret > text.length) return null;

  let start = caret;
  while (start > 0) {
    const char = charBefore(text, start);
    if (!isTokenChar(char)) break;
    start -= char.length;
  }
  if (start === 0 || text[start - 1] !== "@") return null;
  const at = start - 1;
  if (!canStartMention(text, at)) return null;

  const query = text.slice(at + 1, caret);
  if (!query || query.length > MAX_USERNAME_LENGTH) return null;

  let end = caret;
  while (end < text.length) {
    const char = charAt(text, end);
    if (!isTokenChar(char)) break;
    end += char.length;
  }
  return { start: at, end, query };
}

// `text` with the active token replaced by "@username", followed by a space
// unless whitespace already follows. → { text, caret } (caret after the space).
export function insertMention(text, active, username) {
  const before = text.slice(0, active.start);
  const after = text.slice(active.end);
  const mention = `@${username}`;
  const space = /^\s/.test(after) ? "" : " ";
  return {
    text: before + mention + space + after,
    caret: before.length + mention.length + 1,
  };
}

// The backend's 400 for more than 10 mentioned users is a field error on
// "text"; there's no error code, so its wording is matched. Returns the
// message to show, or null for any other error.
export const MENTION_LIMIT_MESSAGE = "You can mention up to 10 people.";

export function mentionLimitMessage(err) {
  if (err?.response?.status !== 400) return null;
  const messages = err.response.data?.text;
  if (!Array.isArray(messages)) return null;
  return messages.some((m) => /mention up to/i.test(String(m))) ? MENTION_LIMIT_MESSAGE : null;
}
